import { notificationProductKey as key } from "../../utils/productUpdateNotifications";
import { alphaAdminUi } from "./alphaAdminUi";
import React, { useEffect, useId, useRef, useState } from "react";
import service from "../../services/adminProductNotificationMappingsService";
import type { NotificationPortfolio, ProductNotificationPage } from "../../types/productNotificationMappings";
import { describeNotificationError } from "../../utils/adminNotificationForm";
import { formatNumber } from "../../utils/numberUtils";

interface Props {
  userId: string;
  value: NotificationPortfolio | null;
  onChange: (product: NotificationPortfolio | null) => void;
  disabled?: boolean;
}

export default function EligibleNotificationPortfolioPicker({ userId, value, onChange, disabled = false }: Props) {
  const searchId = useId();
  const radioName = useId();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [data, setData] = useState<ProductNotificationPage<NotificationPortfolio> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);
  const abort = useRef<AbortController | null>(null);

  // Invalidate immediately on input, before the debounce/effect. Old results
  // must neither become selectable nor replace a newer error/loading state.
  const invalidate = () => {
    ++request.current;
    abort.current?.abort();
    setData(null);
    setError(null);
    setLoading(true);
  };

  useEffect(() => {
    const token = ++request.current;
    const controller = new AbortController();
    abort.current = controller;
    setData(null);
    setError(null);
    setLoading(true);
    const current = () => token === request.current && !controller.signal.aborted;
    const timer = setTimeout(async () => {
      try {
        const result = await service.eligiblePortfolios(userId, page, search, controller.signal);
        if (!current()) return;
        if (page > Math.max(1, result.totalPages)) {
          setPage(Math.max(1, result.totalPages));
          return;
        }
        setData(result);
      } catch (err) {
        if (current()) setError(describeNotificationError(err).message);
      } finally {
        if (current()) setLoading(false);
      }
    }, search ? 300 : 0);
    const invalidateRequest = () => { ++request.current; };
    return () => { clearTimeout(timer); controller.abort(); invalidateRequest(); };
  }, [userId, page, search, reload]);

  const changePage = (next: number) => {
    invalidate();
    onChange(null);
    setPage(next);
  };

  return (
    <div className="space-y-3">
      <label htmlFor={searchId} className="block text-sm font-semibold text-v7-ink">Find an eligible product</label>
      <input id={searchId} type="search" maxLength={100} placeholder="Search by product or provider" className={alphaAdminUi.input}
        value={search} disabled={disabled} onChange={event => {
          invalidate(); onChange(null); setPage(1); setSearch(event.target.value);
        }} />
      <p className="text-xs text-v7-ink/60">Public and subscriber products are available without a subscription.</p>
      {loading && <p role="status" className="text-sm text-v7-ink/60">Loading eligible products...</p>}
      {error && <div role="alert" className={alphaAdminUi.error}>{error} <button type="button" disabled={disabled} className={alphaAdminUi.secondary}
        onClick={() => { invalidate(); setReload(count => count + 1); }}>Retry products</button></div>}
      {data && <>
        {data.items.length === 0 ? <p className="py-4 text-sm text-v7-ink/60">No eligible products found.</p> : (
          <fieldset disabled={disabled} className="max-h-56 divide-y divide-v7-ink/15 overflow-y-auto rounded-v7-panel border border-v7-ink/15">
            <legend className="sr-only">Eligible products</legend>
            {data.items.map(product => (
              <label key={key(product)} className="flex cursor-pointer items-center gap-3 bg-v7-white p-3 hover:bg-v7-paper">
                <input type="radio" name={radioName} checked={!!value && key(value) === key(product)} onChange={() => onChange(product)} className="accent-v7-bronze-dark" />
                <span>
                  <span className="block text-sm font-semibold text-v7-ink">{product.title || `Product #${product.productId}`}</span>
                  <span className="block text-xs text-v7-ink/60">{product.productType === "BASKET" ? "Basket" : "Portfolio"} - {product.provider?.name || "Provider unavailable"}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-v7-ink/60" aria-label="Eligible product pagination">
          <span>Page {formatNumber(data.currentPage, 0)} of {formatNumber(Math.max(1, data.totalPages), 0)}</span>
          <div className="flex gap-2">
            <button type="button" className={alphaAdminUi.secondary} disabled={disabled || page <= 1} onClick={() => changePage(page - 1)}>Previous products</button>
            <button type="button" className={alphaAdminUi.secondary} disabled={disabled || page >= data.totalPages} onClick={() => changePage(page + 1)}>Next products</button>
          </div>
        </div>
      </>}
    </div>
  );
}
